import { Link, useLocation } from '@tanstack/react-router'
import { Icon } from '@/components/ui/Icon'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import type { MaterialIconName } from '@/components/ui/materialIcons.generated'

interface NavItem {
  path: string
  label: string
  icon: MaterialIconName
  activeIcon: MaterialIconName
}

// Same tabs, order and icons as `_MainShellState.build` in apps/mobile router.dart.
// The mobile Admin tab is disabled there (`_isAdmin => false`), so it is not rendered here either.
function navItems(copy: { home: string; reels: string; chat: string; profile: string }): NavItem[] {
  return [
    { path: '/feed', label: copy.home, icon: 'home_outlined', activeIcon: 'home' },
    { path: '/reels', label: copy.reels, icon: 'smart_display_outlined', activeIcon: 'smart_display' },
    { path: '/chat', label: copy.chat, icon: 'chat_bubble_outline', activeIcon: 'chat_bubble' },
    { path: '/profile', label: copy.profile, icon: 'person_outline', activeIcon: 'person' },
  ]
}

interface BottomNavProps {
  /** Live unread count of the Chat tab. Wired to the chat runtime in the chat phase. */
  chatUnreadCount?: number
}

function isActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`)
}

export function BottomNav({ chatUnreadCount = 0 }: BottomNavProps) {
  const { pathname } = useLocation()
  const { strings } = useLanguage()

  return (
    <nav className="fixed bottom-0 left-1/2 z-50 w-full max-w-[480px] -translate-x-1/2 bg-lm-surface border-t border-lm-on-surface/10 pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around py-2">
        {navItems(strings.nav).map((item) => {
          const active = isActive(pathname, item.path)
          const badge = item.path === '/chat' ? chatUnreadCount : 0

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`relative flex flex-col items-center before:absolute before:-inset-x-3 before:-inset-y-2 before:content-[''] ${
                active ? 'text-lm-primary' : 'text-lm-on-surface-variant'
              }`}
            >
              <span className="relative">
                <Icon name={active ? item.activeIcon : item.icon} size={24} />
                {badge > 0 && (
                  <span
                    data-testid="nav-badge"
                    className="absolute -right-1 -top-1 flex min-h-[14px] min-w-[14px] items-center justify-center rounded-full bg-lm-primary p-0.5 text-label-sm font-bold text-lm-on-surface"
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              <span className="mt-1 text-label-sm">{item.label}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
